/**
 * PayPlus Stage B helpers — browser return URLs, pending booking storage, confirm poll.
 */
import { fetchBookingById } from '../api/bookingApi';
import {
  postPayplusGenerateLink,
  type PayplusGenerateLinkResult,
} from '../api/payplusApi';
import { fetchProfile } from '../api/profileApi';
import { getStoredLoginIdentifier, getSessionToken } from '../auth/sessionToken';
import { publicPath } from '../publicPath';
import { extractProfileDisplaySummary } from './profileDisplaySummary';

export const BOOKING_STATUS_PENDING_ID = 1;
export const BOOKING_STATUS_CONFIRMED_ID = 2;

const PENDING_PAYPLUS_STORAGE_KEY = 'ofrontend.payplusPendingBooking';

export type PayplusReturnOutcome = 'success' | 'failure' | 'cancel';

export type PayplusPendingCheckout = {
  bookingId: string;
  pageRequestUid: string | null;
  createdAtMs: number;
  /**
   * When checkout started from an asset→book flow, restore this asset after
   * PayPlus cancel/failure (matches Bit/credits cancel → back to asset).
   */
  returnAssetId: string | null;
};

export type PayplusCustomerFields = {
  customer_name: string;
  email: string;
  phone: string | null;
};

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function isLocalDevHostname(hostname: string): boolean {
  const h = hostname.trim().toLowerCase();
  return h === 'localhost' || h === '127.0.0.1' || h === '[::1]' || h === '::1';
}

/**
 * PayPlus requires absolute public HTTPS return URLs (localhost / 127.0.0.1 → 403).
 * Override with `REACT_APP_PAYPLUS_RETURN_ORIGIN` (e.g. https://dev.offizz.ai or an ngrok URL).
 */
export function resolvePayplusReturnOrigin(
  locationOrigin: string | null | undefined = typeof window !== 'undefined'
    ? window.location?.origin
    : undefined,
): string {
  const fromEnv = (process.env.REACT_APP_PAYPLUS_RETURN_ORIGIN ?? '').trim().replace(/\/$/, '');
  if (fromEnv) {
    return fromEnv;
  }

  const raw = (locationOrigin ?? '').trim().replace(/\/$/, '');
  if (raw) {
    try {
      const url = new URL(raw);
      if (!isLocalDevHostname(url.hostname)) {
        return raw;
      }
    } catch {
      /* fall through */
    }
  }

  return 'https://dev.offizz.ai';
}

function appOrigin(): string {
  return resolvePayplusReturnOrigin();
}

/** Absolute HTTPS redirect URLs PayPlus sends the browser back to (never localhost). */
export function buildPayplusRefUrls(
  bookingId: string,
  options?: { returnAssetId?: string | null },
): {
  refURL_success: string;
  refURL_failure: string;
  refURL_cancel: string;
} {
  const id = bookingId.trim();
  const origin = appOrigin();
  const params = new URLSearchParams();
  params.set('booking', id);
  const assetId =
    typeof options?.returnAssetId === 'string' && options.returnAssetId.trim()
      ? options.returnAssetId.trim()
      : '';
  if (assetId) {
    // Survive cross-origin returns (sessionStorage does not) and power cancel → asset.
    params.set('asset', assetId);
  }
  const q = params.toString();
  return {
    refURL_success: `${origin}${publicPath(`/payment/success?${q}`)}`,
    refURL_failure: `${origin}${publicPath(`/payment/failure?${q}`)}`,
    refURL_cancel: `${origin}${publicPath(`/payment/cancel?${q}`)}`,
  };
}

/** PayPlus-friendly Israeli mobile digits (optional). */
export function normalizePayplusPhone(raw: string | null | undefined): string | null {
  if (!raw) {
    return null;
  }
  let digits = raw.replace(/\D+/g, '');
  if (digits.startsWith('972') && digits.length >= 11) {
    digits = `0${digits.slice(3)}`;
  }
  // Standard IL mobile: 05xxxxxxxx (10 digits)
  if (/^05\d{8}$/.test(digits)) {
    return digits;
  }
  return null;
}

export function rememberPayplusPendingCheckout(pending: PayplusPendingCheckout): void {
  try {
    sessionStorage.setItem(PENDING_PAYPLUS_STORAGE_KEY, JSON.stringify(pending));
  } catch {
    /* private mode */
  }
}

export function readPayplusPendingCheckout(): PayplusPendingCheckout | null {
  try {
    const raw = sessionStorage.getItem(PENDING_PAYPLUS_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!isPlainObject(parsed)) {
      return null;
    }
    const bookingId =
      typeof parsed.bookingId === 'string' && parsed.bookingId.trim()
        ? parsed.bookingId.trim()
        : null;
    if (!bookingId) {
      return null;
    }
    return {
      bookingId,
      pageRequestUid:
        typeof parsed.pageRequestUid === 'string' && parsed.pageRequestUid.trim()
          ? parsed.pageRequestUid.trim()
          : null,
      createdAtMs:
        typeof parsed.createdAtMs === 'number' && Number.isFinite(parsed.createdAtMs)
          ? parsed.createdAtMs
          : Date.now(),
      returnAssetId:
        typeof parsed.returnAssetId === 'string' && parsed.returnAssetId.trim()
          ? parsed.returnAssetId.trim()
          : null,
    };
  } catch {
    return null;
  }
}

export function clearPayplusPendingCheckout(): void {
  try {
    sessionStorage.removeItem(PENDING_PAYPLUS_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Parse `/payment/success|failure|cancel?booking=&asset=` (PUBLIC_URL-aware). */
export function parsePayplusReturnFromLocation(
  pathname: string,
  search: string,
): {
  outcome: PayplusReturnOutcome;
  bookingId: string | null;
  returnAssetId: string | null;
} | null {
  const base = (process.env.PUBLIC_URL ?? '').replace(/\/$/, '');
  let rel = pathname || '/';
  if (base && (rel === base || rel.startsWith(`${base}/`))) {
    rel = rel === base || rel === `${base}/` ? '/' : rel.slice(base.length) || '/';
  }
  const normalized = rel.length > 1 && rel.endsWith('/') ? rel.slice(0, -1) : rel || '/';

  let outcome: PayplusReturnOutcome | null = null;
  if (normalized === '/payment/success') {
    outcome = 'success';
  } else if (normalized === '/payment/failure') {
    outcome = 'failure';
  } else if (normalized === '/payment/cancel') {
    outcome = 'cancel';
  }
  if (!outcome) {
    return null;
  }

  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const fromQuery =
    params.get('booking')?.trim() ||
    params.get('bookingId')?.trim() ||
    params.get('booking_id')?.trim() ||
    null;
  const assetFromQuery =
    params.get('asset')?.trim() ||
    params.get('assetId')?.trim() ||
    params.get('asset_id')?.trim() ||
    null;
  const pending = readPayplusPendingCheckout();
  return {
    outcome,
    bookingId: fromQuery || pending?.bookingId || null,
    returnAssetId: assetFromQuery || pending?.returnAssetId || null,
  };
}

export async function resolvePayplusCustomerFields(): Promise<PayplusCustomerFields> {
  const profileRaw = await fetchProfile();
  const loginId = getStoredLoginIdentifier();
  const summary = extractProfileDisplaySummary(
    isPlainObject(profileRaw) ? profileRaw : null,
    null,
    loginId,
    getSessionToken(),
  );

  const nameParts = [summary.firstName, summary.lastName].filter(
    (p): p is string => typeof p === 'string' && p.trim().length > 0,
  );
  const customer_name =
    (nameParts.length > 0 ? nameParts.join(' ') : summary.displayName.trim()) || 'Customer';

  const email = summary.email?.trim() || '';
  if (!email) {
    throw new Error('Add an email on your profile before paying.');
  }

  const phone = normalizePayplusPhone(summary.phone);
  return { customer_name, email, phone };
}

export function bookingRecordIsPaymentConfirmed(booking: Record<string, unknown>): boolean {
  const statusId = booking.status_id ?? booking.statusId;
  if (statusId === BOOKING_STATUS_CONFIRMED_ID || statusId === '2') {
    return true;
  }
  if (typeof statusId === 'string' && statusId.trim() === '2') {
    return true;
  }
  const nameRaw = booking.status_name ?? booking.statusName ?? booking.status;
  if (typeof nameRaw === 'string' && nameRaw.trim()) {
    const lower = nameRaw.trim().toLowerCase();
    if (lower === 'confirmed' || lower.includes('confirm')) {
      return true;
    }
  }
  return false;
}

export function bookingRecordIsPaymentPending(booking: Record<string, unknown>): boolean {
  if (bookingRecordIsPaymentConfirmed(booking)) {
    return false;
  }
  const statusId = booking.status_id ?? booking.statusId;
  if (statusId === BOOKING_STATUS_PENDING_ID || statusId === '1') {
    return true;
  }
  if (typeof statusId === 'string' && statusId.trim() === '1') {
    return true;
  }
  const nameRaw = booking.status_name ?? booking.statusName ?? booking.status;
  if (typeof nameRaw === 'string' && nameRaw.trim()) {
    const lower = nameRaw.trim().toLowerCase();
    if (lower.includes('pending') || lower.includes('ממתין')) {
      return true;
    }
  }
  return false;
}

/** Pending reservation that still needs a successful PayPlus (or other) charge. */
export function bookingRecordNeedsPayment(booking: Record<string, unknown>): boolean {
  if (!bookingRecordIsPaymentPending(booking)) {
    return false;
  }
  const total = booking.total_price ?? booking.totalPrice;
  if (typeof total === 'number' && Number.isFinite(total)) {
    return total > 0;
  }
  if (typeof total === 'string' && total.trim()) {
    const n = Number(total.replace(',', '.'));
    return Number.isFinite(n) && n > 0;
  }
  // Unknown total but still Pending — treat as unpaid until Confirmed.
  return true;
}


export function unwrapBookingRecord(raw: unknown): Record<string, unknown> | null {
  if (!isPlainObject(raw)) {
    return null;
  }
  if (isPlainObject(raw.booking)) {
    return raw.booking;
  }
  if (isPlainObject(raw.data)) {
    return unwrapBookingRecord(raw.data) ?? raw.data;
  }
  return raw;
}

export type PollBookingPaymentOptions = {
  bookingId: string;
  /** Max attempts (default 12). */
  maxAttempts?: number;
  /** Delay between attempts in ms (default 2000). */
  intervalMs?: number;
  signal?: AbortSignal;
};

export type PollBookingPaymentResult =
  | { status: 'confirmed'; booking: Record<string, unknown> }
  | { status: 'pending'; booking: Record<string, unknown> | null }
  | { status: 'aborted' };

export async function pollBookingUntilPaymentConfirmed(
  options: PollBookingPaymentOptions,
): Promise<PollBookingPaymentResult> {
  const bookingId = options.bookingId.trim();
  const maxAttempts = options.maxAttempts ?? 12;
  const intervalMs = options.intervalMs ?? 2000;
  let last: Record<string, unknown> | null = null;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (options.signal?.aborted) {
      return { status: 'aborted' };
    }
    try {
      const raw = await fetchBookingById(bookingId);
      const booking = unwrapBookingRecord(raw);
      last = booking;
      if (booking && bookingRecordIsPaymentConfirmed(booking)) {
        return { status: 'confirmed', booking };
      }
    } catch {
      /* retry — callback can lag */
    }
    if (attempt < maxAttempts - 1) {
      await new Promise<void>((resolve) => {
        const t = window.setTimeout(() => resolve(), intervalMs);
        options.signal?.addEventListener(
          'abort',
          () => {
            window.clearTimeout(t);
            resolve();
          },
          { once: true },
        );
      });
    }
  }
  if (options.signal?.aborted) {
    return { status: 'aborted' };
  }
  return { status: 'pending', booking: last };
}

/** Create PayPlus page link for a pending booking and remember it for the return hop. */
export async function startPayplusCheckoutForBooking(
  bookingId: string,
  options?: { returnAssetId?: string | null },
): Promise<PayplusGenerateLinkResult> {
  const id = bookingId.trim();
  if (!id) {
    throw new Error('Booking ID is required.');
  }
  const returnAssetId =
    typeof options?.returnAssetId === 'string' && options.returnAssetId.trim()
      ? options.returnAssetId.trim()
      : null;
  const customer = await resolvePayplusCustomerFields();
  const refs = buildPayplusRefUrls(id, { returnAssetId });
  const result = await postPayplusGenerateLink({
    booking_id: id,
    customer_name: customer.customer_name,
    email: customer.email,
    ...(customer.phone ? { phone: customer.phone } : {}),
    ...refs,
  });
  rememberPayplusPendingCheckout({
    bookingId: id,
    pageRequestUid: result.page_request_uid,
    createdAtMs: Date.now(),
    returnAssetId,
  });
  return result;
}

/**
 * Replace the current history entry with the asset browse URL so browser Back from
 * PayPlus returns to the asset (not My Bookings).
 */
export function plantPayplusBrowserBackToAsset(
  returnAssetId: string | null | undefined,
): void {
  if (typeof window === 'undefined') {
    return;
  }
  const assetId =
    typeof returnAssetId === 'string' && returnAssetId.trim() ? returnAssetId.trim() : '';
  if (!assetId) {
    return;
  }
  try {
    const browseUrl = publicPath(`/browse?asset=${encodeURIComponent(assetId)}`);
    window.history.replaceState(window.history.state, document.title, browseUrl);
  } catch {
    /* ignore */
  }
}

/**
 * Plant asset Back target (if any), then navigate to the hosted PayPlus payment page.
 */
export function openPayplusPaymentPage(
  paymentPageLink: string,
  options?: { returnAssetId?: string | null },
): void {
  const url = paymentPageLink.trim();
  if (!url) {
    throw new Error('Missing payment_page_link.');
  }
  if (typeof window === 'undefined') {
    return;
  }
  plantPayplusBrowserBackToAsset(options?.returnAssetId);
  window.location.assign(url);
}
